/**
 * ReviewImportStep
 *
 * The merged final step of the contact-import wizard. It owns a single internal
 * phase state and orchestrates the full import sequence end-to-end:
 *
 *   summary   → review counts, then "Choose destination" opens the
 *               ImportOptionsModal (destination + campaign + optional tags)
 *   importing → runs the pipeline, surfacing real status messages + progress %
 *   complete  → success screen with result stat grid + CTAs
 *
 * Import pipeline (PRESERVED exactly from the former ImportStep):
 *   markRowsCustomOther → applyTagsToRows → updateImportPreview
 *   → performSetCompanies → pollUntilIdle → performSetContacts → pollUntilIdle
 *   → re-fetch preview → count rows where CONTACT_SET != -1 as created.
 *
 * When the chosen destination is a campaign, the imported companies (distinct
 * SET_CHAIN_PROXY_ID values from the re-fetched preview) are assigned to that
 * campaign after a successful import; a failure there is non-blocking.
 *
 * Status messages map to the real sequence:
 *   "Creating companies…"  during performSetCompanies + poll
 *   "Importing contacts…"  during performSetContacts + poll
 *   "Finalizing…"          during the final re-fetch / count.
 */
import React, { Dispatch, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  CopyX,
  Database,
  Loader2,
  RotateCcw,
  Tag,
  TriangleAlert,
  Users,
} from 'lucide-react'
import { cn } from 'components/UI/cn'
import { useMountEffect } from 'hooks/useMountEffect'
import useApiService from 'services/api'
import { InternalColumns } from 'features/importerv2/importer-types'
import {
  fetchImportPreviewData,
  performSetCompanies,
  performSetContacts,
  updateImportPreview,
} from 'features/importerv2/importer-api'
import { fromRowsJSON, toRowsJSON } from 'features/importerv2/ImporterViewModel'
import { markRowsCustomOther } from '../markCustomOther'
import { applyTagsToRows } from '../applyTagsToRows'
import { pollUntilIdle } from '../pollImport'
import {
  validateContactRows,
  findInFileDuplicateRowIds,
  RowIssue,
} from '../contactValidation'
import { performUndoImport, isUndoWindowExpiredError } from '../undoImport'
import { DEFAULT_COMPANY_SUBCATEGORY_SLUG } from '../contactImportFields'
import { WizardState, WizardAction } from '../wizardState'
import { ImportOptionsModal, ImportOptions } from './ImportOptionsModal'
import { StatGrid, StatItem } from './StatGrid'

// ---------------------------------------------------------------------------
// Phase + status constants
// ---------------------------------------------------------------------------

type Phase = 'summary' | 'importing' | 'complete'

/** Status messages shown during the importing phase, in pipeline order. */
const STATUS = {
  preparing: 'Preparing import…',
  companies: 'Creating companies…',
  contacts: 'Importing contacts…',
  finalizing: 'Finalizing…',
} as const

/**
 * Seconds the undo affordance stays available after the import completes.
 * The backend allows a slightly longer grace window (15 min) — see
 * `server/importengine/service/v2/undo_import.py` (UNDO_WINDOW).
 */
const UNDO_WINDOW_SECONDS = 600

/** Format a number of seconds as m:ss (e.g. 9:05). */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// InlineAlert
// ---------------------------------------------------------------------------

/**
 * Reusable alert banner for inline error and loading messages within the step.
 *
 * @param variant - 'error' shows red border/bg; 'info' shows muted border/bg.
 * @param children - Alert message content.
 */
function InlineAlert({
  variant,
  children,
}: {
  variant: 'error' | 'info'
  children: React.ReactNode
}) {
  return (
    <div
      className={cn('rounded-xl border px-4 py-3 text-sm', {
        'border-error-border bg-error-bg text-error': variant === 'error',
        'border-line bg-surface-muted text-fg-muted': variant === 'info',
      })}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// UndoControl — the complete-phase "Undo this import (m:ss)" affordance.
// ---------------------------------------------------------------------------

type UndoStatus = 'idle' | 'pending' | 'done' | 'expired'

/**
 * Renders the undo affordance for the complete phase:
 * - Counts down from `UNDO_WINDOW_SECONDS` (mm:ss); at 0:00 the window closes
 *   and the button is hidden.
 * - On click, calls `performUndoImport(importNumber)`, shows a pending state,
 *   then "Import undone." on success.
 * - A backend 409 (window expired) is surfaced as an inline message.
 *
 * Rendered only in the complete phase and keyed to remount per import, so the
 * one allowed `useMountEffect` cleanly sets up and tears down its interval.
 */
function UndoControl({
  importNumber,
  onUndone,
}: {
  importNumber: number
  onUndone: () => void
}) {
  const [secondsLeft, setSecondsLeft] = useState(UNDO_WINDOW_SECONDS)
  const [status, setStatus] = useState<UndoStatus>('idle')

  useMountEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval)
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  })

  const handleUndo = async () => {
    if (status === 'pending' || status === 'done') return
    setStatus('pending')
    try {
      await performUndoImport(importNumber)
      setStatus('done')
      onUndone()
    } catch (err) {
      // Window expired server-side (409) — surface inline; the countdown may
      // not have hit 0 locally due to clock skew / latency.
      setStatus(isUndoWindowExpiredError(err) ? 'expired' : 'idle')
    }
  }

  if (status === 'done') {
    return <p className="text-xs text-fg-muted">Import undone.</p>
  }

  if (status === 'expired') {
    return (
      <p className="text-xs text-fg-muted">
        The undo window has expired — this import can no longer be undone.
      </p>
    )
  }

  // Window closed: hide the affordance entirely.
  if (secondsLeft <= 0) return null

  return (
    <button
      type="button"
      onClick={handleUndo}
      disabled={status === 'pending'}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-fg-muted transition-colors',
        status === 'pending'
          ? 'cursor-not-allowed opacity-70'
          : 'hover:text-error'
      )}
    >
      {status === 'pending' ? (
        <>
          <Loader2 className="size-3 animate-spin" />
          Undoing import…
        </>
      ) : (
        <>
          <RotateCcw className="size-3" />
          Undo this import
          <span className="ml-0.5 tabular-nums">
            ({formatCountdown(secondsLeft)})
          </span>
        </>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// ReviewImportStep
// ---------------------------------------------------------------------------

interface ReviewImportStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

export function ReviewImportStep({ state, dispatch }: ReviewImportStepProps) {
  const navigate = useNavigate()
  const api = useApiService()
  const { data: companyCategories, isLoading: isCategoriesLoading } =
    api.useGetCompanyCategories()

  // Derive the "Other/Other" subcategory used for custom companies.
  const otherType = useMemo(() => {
    if (!companyCategories) return undefined
    for (const cat of companyCategories) {
      for (const sub of cat.sub_categories ?? []) {
        if (sub.slug === DEFAULT_COMPANY_SUBCATEGORY_SLUG) {
          return { category_id: cat.id, value: sub.id }
        }
      }
    }
    return undefined
  }, [companyCategories])

  // Local phase / progress state.
  const [phase, setPhase] = useState<Phase>('summary')
  const [progress, setProgress] = useState(0)
  const [statusMsg, setStatusMsg] = useState<string>(STATUS.preparing)
  const [error, setError] = useState<string | null>(null)
  // The "Choose destination" modal (destination + campaign + tags lives there).
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  // The campaign the contacts were imported into, when destination === campaign.
  // Drives the complete-phase summary line + the "View campaign" CTA. Null for
  // CRM-only imports.
  const [importedCampaignName, setImportedCampaignName] = useState<
    string | null
  >(null)
  const [importedCampaignId, setImportedCampaignId] = useState<number | null>(
    null
  )
  // True when the import succeeded but assigning the imported companies to the
  // chosen campaign failed — surfaced as a non-blocking note (contacts are in).
  // True once the user has undone this import — contacts are gone, so the
  // success copy and CTAs swap to a "start over" framing.
  const [undone, setUndone] = useState(false)
  // Set when an import fails AFTER the backend pipeline was triggered. The
  // Celery task may still be running, so re-running could double-process — we
  // lock the import and tell the user to refresh and check, rather than offer a
  // clean retry. Errors before the pipeline starts leave this false (safe retry).
  const [importLocked, setImportLocked] = useState(false)

  // isRunning ref — updated in the click handler (no effect needed).
  const isRunningRef = useRef(false)
  // Tracks whether the backend pipeline (set-companies onward) was triggered in
  // the current run — used by the catch handler to decide lock vs. retry.
  const pipelineStartedRef = useRef(false)

  // Navigation guard: register once on mount, read isRunningRef in the handler.
  useMountEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isRunningRef.current) return
      e.preventDefault()
    }

    const handlePopState = (e: PopStateEvent) => {
      if (!isRunningRef.current) return
      e.preventDefault()
      const leave = window.confirm(
        'Import is in progress. Are you sure you want to leave? This may cause data loss.'
      )
      if (!leave) {
        window.history.pushState(null, '', window.location.href)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  })

  // ---------------------------------------------------------------------------
  // Derived counts
  // ---------------------------------------------------------------------------

  const totalRows = state.importData?.rows.length ?? 0
  const includedCount = totalRows - state.excludedRowIds.size
  const excludedCount = state.excludedRowIds.size

  const includedRowIds: ReadonlySet<string> = useMemo(() => {
    if (!state.importData) return new Set<string>()
    const ids = new Set<string>()
    for (const row of state.importData.rows) {
      const id = String(row[InternalColumns.ROW_ID])
      if (!state.excludedRowIds.has(id)) {
        ids.add(id)
      }
    }
    return ids
  }, [state.importData, state.excludedRowIds])

  // Validation-derived counts (recomputed from rows — mirrors ValidateStep, no
  // useEffect). Used to populate the summary stat grid (errors / duplicates).
  const { errorCount, duplicateCount } = useMemo(() => {
    if (!state.importData) return { errorCount: 0, duplicateCount: 0 }
    const { rows, headers } = state.importData
    const issues: Record<string, RowIssue[]> = validateContactRows(
      rows,
      headers
    )
    let errors = 0
    for (const rowIssues of Object.values(issues)) {
      if (rowIssues.some((i) => i.severity === 'error')) errors++
    }
    // Duplicates = the same union the Validate tab shows: in-file duplicates
    // (same email on 2+ rows) ∪ CRM duplicates (IS_EXISTING_CRM_CONTACT).
    // validateContactRows does NOT emit a "Duplicate" error, so deriving this
    // from issues would always be 0.
    const duplicateIds = new Set<string>(
      findInFileDuplicateRowIds(rows, headers)
    )
    for (const r of rows) {
      if (r[InternalColumns.IS_EXISTING_CRM_CONTACT] === true) {
        duplicateIds.add(String(r[InternalColumns.ROW_ID]))
      }
    }
    return { errorCount: errors, duplicateCount: duplicateIds.size }
  }, [state.importData])

  // ---------------------------------------------------------------------------
  // Import pipeline (preserved byte-for-byte; only status messages added)
  // ---------------------------------------------------------------------------

  /**
   * Runs the full import pipeline with the options chosen in the modal.
   *
   * Tags from the modal are applied via the existing `applyTagsToRows` path
   * (unchanged). When the destination is a campaign, the backend assigns the
   * imported companies to it (and writes any custom columns) atomically during
   * set-contacts.
   */
  const handleStartImport = async (opts: ImportOptions) => {
    if (!state.importData || !state.importNumber || !otherType) return
    if (phase === 'importing') return

    const tagNames = opts.tagNames
    setImportedCampaignName(null)
    setImportedCampaignId(null)

    setPhase('importing')
    setError(null)
    setProgress(0)
    setStatusMsg(STATUS.preparing)
    isRunningRef.current = true
    pipelineStartedRef.current = false

    try {
      // Step a: mark all rows as custom-Other, set READY flags.
      const marked = markRowsCustomOther(
        state.importData,
        state.excludedRowIds,
        otherType
      )

      // Step b: inject tag names into contact_label column (if any).
      const withTags =
        tagNames.length > 0
          ? applyTagsToRows(marked, includedRowIds, tagNames)
          : marked

      // Step c: persist updated rows to the backend.
      await updateImportPreview(state.importNumber, {
        import_data: { data: toRowsJSON(withTags) },
      })

      // Step d: trigger set-companies then poll until idle. From here on the
      // backend pipeline is in flight — a failure must lock (not offer retry).
      pipelineStartedRef.current = true
      setStatusMsg(STATUS.companies)
      setProgress(30)
      await performSetCompanies(state.importNumber)
      await pollUntilIdle(state.importNumber)

      // Step e: trigger set-contacts then poll until idle. When importing to a
      // campaign (locked entry OR campaign chosen in the modal), send its id so
      // the backend assigns the companies AND writes any custom campaign-column
      // values atomically — custom columns are campaign-scoped, so they only
      // get written when set-contacts receives a campaign_id.
      const campaignIdForImport =
        opts.destination === 'campaign' ? opts.campaignId : null
      setStatusMsg(STATUS.contacts)
      setProgress(65)
      await performSetContacts(
        state.importNumber,
        state.existingContactMode,
        campaignIdForImport,
        state.customColumns
      )
      await pollUntilIdle(state.importNumber)

      // Step f: re-fetch the preview to get accurate created/failed counts.
      // The backend writes the created Contact id into CONTACT_SET for each row
      // it successfully imports, leaving -1 for rows it skipped or failed.
      setStatusMsg(STATUS.finalizing)
      setProgress(95)
      let created = includedCount // fallback: assume all succeeded
      try {
        const finalPreview = await fetchImportPreviewData(state.importNumber)
        const finalData = fromRowsJSON(finalPreview.data)
        created = finalData.rows.filter(
          (r) =>
            r[InternalColumns.CONTACT_SET] !== -1 &&
            r[InternalColumns.CONTACT_SET] != null
        ).length
      } catch {
        // Re-fetch failed — fall back to optimistic count (created = includedCount).
      }
      const failed = Math.max(0, includedCount - created)
      setProgress(100)

      // Step g: compute result counts.
      const result = {
        created,
        excluded: excludedCount,
        failed,
      }

      // Step h: store result; the contacts are now imported regardless of what
      // happens with the campaign assignment below.
      dispatch({ type: 'SET_RESULT', result })

      // Step i: record the campaign for the success screen. The backend already
      // assigned the imported companies to the campaign (and wrote any custom
      // columns) via set-contacts' campaign_id, for both the locked entry and
      // the Contacts-page path. Only when at least one contact was actually
      // imported — if every row was excluded (created === 0) nothing was
      // assigned, so don't claim a campaign on the success screen.
      if (
        opts.destination === 'campaign' &&
        opts.campaignId != null &&
        created > 0
      ) {
        setImportedCampaignName(opts.campaignName)
        setImportedCampaignId(opts.campaignId)
      }

      setPhase('complete')
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'An unexpected error occurred during import.'
      setPhase('summary')
      if (pipelineStartedRef.current) {
        // The import was already triggered on the backend and the task may
        // still be running. Lock the flow so a retry can't double-process —
        // the user should refresh to see whether the import actually finished.
        setImportLocked(true)
        setError(
          'The import started but did not confirm completion. Refresh the page to check whether your contacts were imported before trying again.'
        )
      } else {
        setError(msg)
      }
    } finally {
      isRunningRef.current = false
    }
  }

  // ---------------------------------------------------------------------------
  // Derived render flags
  // ---------------------------------------------------------------------------

  const isReady = !isCategoriesLoading && !!otherType
  const canStart =
    isReady && includedCount > 0 && phase !== 'importing' && !importLocked
  const result = state.result

  // ---------------------------------------------------------------------------
  // Summary phase stat grid
  // ---------------------------------------------------------------------------

  const summaryStats: StatItem[] = [
    {
      icon: <Users className="size-5 text-fg-secondary" />,
      value: totalRows,
      label: 'Total rows',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
    {
      icon: <Database className="size-5 text-brand" />,
      value: includedCount,
      label: 'To import',
      bg: 'bg-success-bg-subtle',
      textColor: 'text-success-strong',
    },
    {
      icon: <Ban className="size-5 text-fg-secondary" />,
      value: excludedCount,
      label: 'Excluded',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
    {
      icon: <CopyX className="size-5 text-fg-secondary" />,
      value: duplicateCount,
      label: 'Duplicates',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
    {
      icon: <TriangleAlert className="size-5 text-warning" />,
      value: errorCount,
      label: 'Errors',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
  ]

  const completeStats: StatItem[] = [
    {
      icon: <Users className="size-5 text-success" />,
      value: result?.created ?? 0,
      label: 'Contacts imported',
      bg: 'bg-success-bg-subtle',
      textColor: 'text-success-strong',
    },
    {
      icon: <Ban className="size-5 text-fg-muted" />,
      value: result?.excluded ?? 0,
      label: 'Rows excluded',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
    {
      icon: <AlertCircle className="size-5 text-fg-muted" />,
      value: result?.failed ?? 0,
      label: 'Failed',
      bg: 'bg-surface-sunken',
      textColor: 'text-fg',
    },
  ]

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {showOptionsModal && (
        <ImportOptionsModal
          lockedCampaignId={state.campaignId}
          hasCustomColumns={state.customColumns.length > 0}
          onClose={() => setShowOptionsModal(false)}
          onConfirm={(opts) => {
            setShowOptionsModal(false)
            void handleStartImport(opts)
          }}
        />
      )}

      <div className="absolute inset-0 flex flex-col">
        <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-5">
          {/* ── Summary phase ── */}
          {phase === 'summary' && (
            <>
              {/* Stat grid */}
              <StatGrid items={summaryStats} />

              {/* What Happens Next */}
              <div className="rounded-xl border border-success-bg bg-success-bg-subtle px-4 py-3.5">
                <p className="text-xs font-semibold text-brand uppercase tracking-wide mb-2.5">
                  What happens next
                </p>
                <ul className="flex flex-col gap-2">
                  <li className="flex items-start gap-2.5">
                    <Users className="size-3.5 text-fg-muted mt-0.5 shrink-0" />
                    <p className="text-xs text-fg-secondary">
                      <span className="font-medium text-fg">
                        {includedCount.toLocaleString()} contact
                        {includedCount !== 1 ? 's' : ''} will be added to First
                        Bite
                      </span>{' '}
                      and linked to their companies.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Database className="size-3.5 text-fg-muted mt-0.5 shrink-0" />
                    <p className="text-xs text-fg-secondary">
                      <span className="font-medium text-fg">
                        Companies that don&rsquo;t match an existing First Bite
                        company will be created
                      </span>{' '}
                      as custom companies under the &ldquo;Other&rdquo;
                      category.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Tag className="size-3.5 text-fg-muted mt-0.5 shrink-0" />
                    <p className="text-xs text-fg-secondary">
                      Choose a destination and any optional tags on the next
                      step — tags are applied to every imported contact. You can
                      undo this import shortly after it finishes.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Categories loading / missing warning */}
              {isCategoriesLoading && (
                <InlineAlert variant="info">
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin shrink-0" />
                    Loading company type data...
                  </span>
                </InlineAlert>
              )}

              {!isCategoriesLoading && !otherType && (
                <InlineAlert variant="error">
                  Unable to resolve the default company type. Please refresh and
                  try again.
                </InlineAlert>
              )}

              {/* Runtime error */}
              {error && <InlineAlert variant="error">{error}</InlineAlert>}
            </>
          )}

          {/* ── Importing phase ── */}
          {phase === 'importing' && (
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="size-16 rounded-full bg-success-bg-subtle flex items-center justify-center">
                <Loader2 className="size-8 text-brand animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-fg">
                  Importing {includedCount.toLocaleString()} contact
                  {includedCount !== 1 ? 's' : ''}...
                </p>
                <p
                  className="text-sm text-fg-secondary mt-1"
                  aria-live="polite"
                >
                  {statusMsg}
                </p>
              </div>
              <div className="w-full max-w-sm">
                <div
                  className="h-2.5 rounded-full bg-line overflow-hidden"
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full bg-brand rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-1.5">
                  <span className="text-xs text-fg-muted">{progress}%</span>
                </div>
              </div>
              <p className="text-xs text-fg-muted">
                Please keep this page open until the import completes.
              </p>
            </div>
          )}

          {/* ── Complete phase ── */}
          {phase === 'complete' && (
            <div className="flex flex-col items-center justify-center gap-8 py-10">
              {/* Success icon */}
              <div className="size-16 rounded-full bg-success-bg flex items-center justify-center">
                <CheckCircle2 className="size-9 text-success" strokeWidth={2} />
              </div>

              {/* Header */}
              <div className="text-center">
                {undone ? (
                  <>
                    <h3 className="text-xl font-bold text-fg">Import undone</h3>
                    <p className="text-sm text-fg-secondary mt-1">
                      The imported contacts and any companies created during
                      this import have been removed from First Bite.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-fg">
                      Import Complete!
                    </h3>
                    <p className="text-sm text-fg-secondary mt-1">
                      <span className="font-semibold text-fg">
                        {(result?.created ?? 0).toLocaleString()} contact
                        {(result?.created ?? 0) !== 1 ? 's' : ''}
                      </span>{' '}
                      {(result?.created ?? 0) !== 1 ? 'have' : 'has'} been added
                      to First Bite
                      {importedCampaignName ? (
                        <>
                          {' '}
                          and added to{' '}
                          <span className="font-semibold text-fg">
                            {importedCampaignName}
                          </span>
                        </>
                      ) : null}
                      .
                    </p>
                  </>
                )}
              </div>

              {/* Result stat grid — hidden once undone (counts no longer apply). */}
              {!undone && (
                <StatGrid items={completeStats} className="w-full max-w-md" />
              )}

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {undone ? (
                  <button
                    type="button"
                    className="px-6 h-11 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand/90 transition-colors shadow-sm"
                    onClick={() => dispatch({ type: 'RESET' })}
                  >
                    Import another →
                  </button>
                ) : (
                  <>
                    {(state.campaignId ?? importedCampaignId) != null && (
                      <button
                        type="button"
                        className="px-6 h-11 rounded-lg border border-line text-fg text-sm font-medium hover:bg-surface-hover transition-colors"
                        onClick={() =>
                          navigate(
                            `/campaigns/${
                              state.campaignId ?? importedCampaignId
                            }/detail`
                          )
                        }
                      >
                        View campaign
                      </button>
                    )}
                    <button
                      type="button"
                      className="px-6 h-11 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand/90 transition-colors shadow-sm"
                      onClick={() => {
                        const cid = state.campaignId ?? importedCampaignId
                        if (cid != null) {
                          navigate(`/campaigns/${cid}/detail?tab=contacts`)
                        } else {
                          navigate('/contacts/all')
                        }
                      }}
                    >
                      View contacts →
                    </button>
                    <button
                      type="button"
                      className="px-6 h-11 rounded-lg border border-line text-fg text-sm font-medium hover:bg-surface-hover transition-colors"
                      onClick={() => dispatch({ type: 'RESET' })}
                    >
                      Import another
                    </button>
                  </>
                )}
              </div>

              {/* Undo affordance — 10-min countdown, keyed to remount per import. */}
              {!undone && state.importNumber != null && (
                <UndoControl
                  key={`undo-${state.importNumber}`}
                  importNumber={state.importNumber}
                  onUndone={() => setUndone(true)}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer — summary phase only (no nav during importing/complete) */}
        {phase === 'summary' && (
          <div className="px-8 py-4 border-t border-line bg-surface flex items-center justify-between shrink-0">
            <button
              type="button"
              onClick={() => dispatch({ type: 'SET_STEP', step: 'validate' })}
              className="px-4 py-2 rounded-lg border border-line text-sm font-medium text-fg hover:bg-surface-hover transition-colors"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setShowOptionsModal(true)}
              disabled={!canStart}
              className={cn(
                'flex items-center gap-2 px-8 py-2.5 rounded-lg text-sm font-bold transition-colors',
                canStart
                  ? 'bg-brand text-white hover:bg-brand/90 shadow-sm'
                  : 'bg-brand/40 text-white cursor-not-allowed'
              )}
            >
              <Database className="size-4" />
              Choose destination
            </button>
          </div>
        )}
      </div>
    </>
  )
}
